import {
	actionEnableGodMode,
	checkGeo,
	checkGeoSuccess,
	checkTerms,
	checkTermsSuccess,
	checkUserName,
	checkUserNameFailed,
	checkUserNameSuccess,
	fetchFavoritePlayers,
	fetchFavoritePlayersSuccess,
	fetchFavoriteSquads,
	fetchFavoriteSquadsSuccess,
	fetchUserRewardsSuccess,
	getCountrySuccess,
	postLoginViaBackDoor,
	postLoginViaBackDoorError,
	postUserLogoutSuccess,
	retrieveUserSuccess,
	setForceTutorialModal,
	setSessionState,
	setViewRewardModalSuccess,
	userLogin, viewWinModalSuccess,
} from "modules/actions/user";
import {IS_SECRET} from "modules/constants";
import {RequestState, type BadgeType} from 'modules/enums';
import {createReducer} from "redux-act";
import {Storage} from "modules/utils";

interface IUserStore {
	loadingState: RequestState;
	sessionState: RequestState;
	geoRequestState: RequestState;
	favoriteRequestState: RequestState;
	checkUserNameRequestState: RequestState;
	checkTermsRequestState: RequestState;
	currentUser: ReturnType<typeof retrieveUserSuccess>["payload"]["user"] | null;
	terms: boolean;
	userHasUserName: boolean;
	isCountryAllowed: boolean;
	country: ReturnType<typeof getCountrySuccess>["payload"];
	favoritePlayers: ReturnType<typeof fetchFavoritePlayersSuccess>["payload"];
	favoriteSquads: ReturnType<typeof fetchFavoriteSquadsSuccess>["payload"];
	isSecretEnabled: boolean;
	godMode: boolean;
	stateCode: string | null;
	countryCode: string | null;
	rewards: BadgeType[];
	ssoData: ReturnType<typeof userLogin>["payload"] | null;
}

const initialState: IUserStore = {
	loadingState: RequestState.IDLE,
	checkTermsRequestState: RequestState.IDLE,
	sessionState: RequestState.IDLE,
	geoRequestState: RequestState.IDLE,
	favoriteRequestState: RequestState.IDLE,
	checkUserNameRequestState: RequestState.IDLE,
	currentUser: null,
	terms: false,
	userHasUserName: false,
	isCountryAllowed: false,
	favoritePlayers: {
		favoritePlayerIds: [],
		mostSelectedPlayerIds: [],
	},
	favoriteSquads: {
		favoriteSquadIds: [],
	},
	country: {
		isoCode: "",
		isoStateCode: "",
	},
	isSecretEnabled: IS_SECRET,
	godMode: false,
	stateCode: null,
	countryCode: null,
	rewards: [],
	ssoData: null,
};

export const user = createReducer({}, initialState);

/**
 * Backdoor user flow;
 */
user.on(postLoginViaBackDoor, (state) => ({
	...state,
	loadingState: RequestState.REQUESTED,
}))
	.on(retrieveUserSuccess, (state, payload) => {
		if( payload.xSid?.length) {
			Storage.SET('xSid', payload.xSid);
		}
		return {
			...state,
			currentUser: payload.user,
			loadingState: RequestState.RECEIVED,
			terms: true,
			countryCode: state.countryCode || payload.user.countryCode,
			stateCode: state.stateCode || payload.user.stateCode,
		}
	})
	.on(setSessionState, (state, payload) => ({
		...state,
		sessionState: payload,
	}))
	.on(postUserLogoutSuccess, () => {
		Storage.REMOVE('xSid');
		return {
			...initialState,
			currentUser: null,
			ssoData: null
		}
	})
	.on(postLoginViaBackDoorError, (state) => ({
		...state,
		loadingState: RequestState.RECEIVED,
	}))
	.on(checkGeo, (state) => ({
		...state,
		geoRequestState: RequestState.REQUESTED,
	}))
	.on(getCountrySuccess, (state, payload) => ({
		...state,
		country: payload,
	}))
	.on(checkGeoSuccess, (state, payload) => ({
		...state,
		geoRequestState: RequestState.RECEIVED,
		isCountryAllowed: payload.isAllowed,
	}))
	.on(checkTerms, (state) => ({
		...state,
		checkTermsRequestState: RequestState.REQUESTED,
	}))
	.on(checkTermsSuccess, (state, payload) => ({
		...state,
		checkTermsRequestState: RequestState.RECEIVED,
		terms: payload.user.terms,
	}))
	.on(checkUserNameFailed, (state) => ({
		...state,
		checkUserNameRequestState: RequestState.RECEIVED,
	}))
	.on(checkUserName, (state) => ({
		...state,
		checkUserNameRequestState: RequestState.REQUESTED,
	}))
	.on(checkUserNameSuccess, (state, payload) => ({
		...state,
		checkUserNameRequestState: RequestState.RECEIVED,
		userHasUserName: payload.user.username,
		stateCode: payload.user.stateCode,
		countryCode: payload.user.countryCode,
	}))
	.on(checkUserNameFailed, (state) => ({
		...state,
		checkUserNameRequestState: RequestState.RECEIVED,
	}))
	.on(fetchFavoritePlayers, (state) => ({
		...state,
		favoriteRequestState: RequestState.REQUESTED,
	}))
	.on(fetchFavoriteSquads, (state) => ({
		...state,
		favoriteRequestState: RequestState.REQUESTED,
	}))
	.on(fetchFavoritePlayersSuccess, (state, payload) => ({
		...state,
		favoritePlayers: payload,
		favoriteRequestState: RequestState.RECEIVED,
	}))
	.on(fetchFavoriteSquadsSuccess, (state, payload) => ({
		...state,
		favoriteSquads: payload,
		favoriteRequestState: RequestState.RECEIVED,
	}))
	.on(setForceTutorialModal, (state, payload) => ({
		...state,
		currentUser: state.currentUser
			? {
					...state.currentUser,
					isTutorialOpenForced: payload,
			  }
			: null,
	}))
	.on(actionEnableGodMode, (state) => ({
		...state,
		godMode: true,
	}))
	.on(setViewRewardModalSuccess, (state, payload) => ({
		...state,
		currentUser: payload.user,
	}))
	.on(userLogin, (state, payload) => ({
		...state,
		ssoData: payload,
	}))
	.on(fetchUserRewardsSuccess, (state, {rewards}) => ({
		...state,
		rewards,
	}))
	.on(viewWinModalSuccess, (state, payload) => ({
		...state,
		currentUser: payload.user
	}));
